import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  FormLabel,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useMemo, useRef, useState } from "react";

import StepBar from "./StepBar";

const sameId = (left, right) =>
  left !== null &&
  left !== undefined &&
  right !== null &&
  right !== undefined &&
  String(left) === String(right);

const processLabel = (processus) =>
  `${processus?.instance || "—"} — ${
    processus?.referentiel_nom || "Référentiel non renseigné"
  } (${processus?.direction_code || "—"})`;

const directionLabel = (direction) =>
  [direction?.code, direction?.name]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .join(" — ") || "Direction sans nom";

const buildReferencePreview = (formData, triggers, nextIndex) => {
  const trigger = triggers.find((item) =>
    sameId(item.trigger_id, formData.trigger_id)
  );
  const dateParts = String(formData.constat_date || "").split("-");
  if (
    !trigger ||
    dateParts.length !== 3 ||
    !Number.isInteger(nextIndex) ||
    nextIndex < 1
  ) {
    return "";
  }

  const code = String(trigger.trigger_code || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return code
    ? `NC_${dateParts[1]}_${dateParts[0]}_${code}_${String(nextIndex).padStart(3, "0")}`
    : "";
};

const fieldLabelSx = {
  color: "#536b91",
  display: "block",
  fontSize: 10,
  fontWeight: 800,
  letterSpacing: "0.06em",
  mb: 0.55,
  textTransform: "uppercase",
  "& .MuiFormLabel-asterisk": { color: "#d32f2f" },
};

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: "8px",
    fontSize: 13,
  },
};

const ConstatStep = ({
  directions,
  directionsError,
  directionsLoading,
  formData,
  nextIndex,
  nextIndexError,
  nextIndexLoading,
  navigationEnabled,
  onChange,
  onProcessusChange,
  onReset,
  onRetryNextIndex,
  onRetryDirections,
  onRetryTriggers,
  onRetryUsers,
  onStepChange,
  onTriggerChange,
  onNext,
  processus,
  savedReference,
  triggers,
  triggersError,
  triggersLoading,
  users,
  usersError,
  usersLoading,
  saving,
}) => {
  const [errors, setErrors] = useState({});
  const triggerRef = useRef(null);
  const dateRef = useRef(null);
  const directionRef = useRef(null);
  const processusRef = useRef(null);
  const descriptionRef = useRef(null);

  const selectedDirection = useMemo(
    () => directions.find((item) => sameId(item.direction_id, formData.direction_id)) || null,
    [directions, formData.direction_id]
  );
  const selectedTrigger = useMemo(
    () => triggers.find((item) => sameId(item.trigger_id, formData.trigger_id)) || null,
    [formData.trigger_id, triggers]
  );
  const selectedProcessus = useMemo(
    () => processus.find((item) => sameId(item.processus_id, formData.processus_id)) || null,
    [formData.processus_id, processus]
  );
  const selectedUser = useMemo(
    () => users.find((item) => sameId(item.id, formData.constat_user)) || null,
    [formData.constat_user, users]
  );
  const orderedProcessus = useMemo(
    () =>
      [...processus].sort(
        (left, right) =>
          Number(left.categorie_ordre || Number.MAX_SAFE_INTEGER) -
            Number(right.categorie_ordre || Number.MAX_SAFE_INTEGER) ||
          String(left.referentiel_nom || "").localeCompare(
            String(right.referentiel_nom || ""),
            "fr",
            { sensitivity: "base" }
          )
      ),
    [processus]
  );
  const persistedReference = String(savedReference || "").trim();
  const referencePreview = persistedReference || buildReferencePreview(formData, triggers, nextIndex);
  const requiresGeneratedReference = !persistedReference;

  const updateField = (name, value) => {
    onChange(name, value);
    setErrors((current) => ({ ...current, [name]: undefined }));
  };

  const validate = () => {
    const nextErrors = {};
    if (!formData.trigger_id) nextErrors.trigger_id = "Le type de déclencheur est obligatoire.";
    if (!formData.constat_date) nextErrors.constat_date = "La date du constat est obligatoire.";
    if (!formData.direction_id) nextErrors.direction_id = "La direction ou antenne est obligatoire.";
    if (!formData.processus_id) nextErrors.processus_id = "Le processus est obligatoire.";
    if (!String(formData.description_constat || "").trim()) {
      nextErrors.description_constat = "La description du constat est obligatoire.";
    }

    setErrors(nextErrors);
    const firstInvalid = [
      ["trigger_id", triggerRef],
      ["constat_date", dateRef],
      ["direction_id", directionRef],
      ["processus_id", processusRef],
      ["description_constat", descriptionRef],
    ].find(([name]) => nextErrors[name]);
    firstInvalid?.[1]?.current?.focus();
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!saving && validate()) onNext();
  };

  const handleReset = () => {
    setErrors({});
    onReset();
  };

  return (
    <Box
      aria-label="Étape du constat"
      role="region"
      sx={{ backgroundColor: "#f1f5fb", minHeight: "calc(100vh - 56px)", p: { xs: 2, sm: 2.5 } }}
    >
      <StepBar
        activeStep={0}
        navigationEnabled={navigationEnabled}
        onStepChange={onStepChange}
      />

      <Paper
        component="form"
        elevation={0}
        noValidate
        onSubmit={handleSubmit}
        sx={{ border: "1px solid #cbd8e6", borderRadius: "12px", mt: 2.5, p: { xs: 2, sm: 2.5 } }}
      >
        <Box sx={{ alignItems: "center", borderBottom: "1px solid #d5e0eb", display: "flex", gap: 1.5, pb: 1.5 }}>
          <Box sx={{ alignItems: "center", backgroundColor: "#e3f7fa", borderRadius: "9px", color: "#173d61", display: "flex", height: 34, justifyContent: "center", width: 34 }}>
            <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
          </Box>
          <Box>
            <Typography color="#10263d" fontSize={14} fontWeight={800}>
              Étape 1 — Constat d&apos;écart
            </Typography>
            <Typography color="#60758a" fontSize={11}>
              Décrire objectivement les faits observés
            </Typography>
          </Box>
        </Box>

        <Alert icon={<span aria-hidden="true"></span>} severity="info" sx={{ backgroundColor: "#dff7fb", border: "1px solid #5fd6e5", color: "#075a68", mt: 2 }}>
          <strong>Règle :</strong> Faits bruts — sans interprétation. Quoi, où, quand, combien.
         
        </Alert>

        <Grid container spacing={1.7} sx={{ mt: 0.2 }}>
          <Grid item md={6} xs={12}>
            <FormLabel required sx={fieldLabelSx}>Type de déclencheur</FormLabel>
            <Autocomplete
              disabled={saving || triggersLoading || Boolean(triggersError)}
              getOptionLabel={(option) =>
                `${option.trigger_code || "—"} — ${option.trigger_name || "Déclencheur sans nom"}`
              }
              isOptionEqualToValue={(option, value) =>
                sameId(option.trigger_id, value.trigger_id)
              }
              loading={triggersLoading}
              noOptionsText="Aucun type de déclencheur"
              onChange={(_, option) => {
                onTriggerChange(option?.trigger_id ?? "");
                setErrors((current) => ({ ...current, trigger_id: undefined }));
              }}
              options={triggers}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(errors.trigger_id)}
                  helperText={errors.trigger_id || triggersError || " "}
                  inputRef={triggerRef}
                  placeholder="Choisir le type de déclencheur"
                  size="small"
                  sx={fieldSx}
                />
              )}
              value={selectedTrigger}
            />
            {triggersLoading && <Typography color="#60758a" fontSize={10}>Chargement des déclencheurs…</Typography>}
            {triggersError && (
              <Button onClick={onRetryTriggers} size="small" sx={{ textTransform: "none" }}>
                Réessayer
              </Button>
            )}
          </Grid>

          <Grid item md={6} xs={12}>
            <FormLabel required sx={fieldLabelSx}>Référence NC</FormLabel>
            <TextField
              error={requiresGeneratedReference && Boolean(nextIndexError)}
              fullWidth
              helperText={(requiresGeneratedReference && nextIndexError) || " "}
              inputProps={{ "aria-label": "Référence NC générée automatiquement", readOnly: true }}
              placeholder={
                requiresGeneratedReference && nextIndexLoading
                  ? "Chargement du prochain numéro…"
                  : "Auto-générée via déclencheur"
              }
              size="small"
              sx={{ ...fieldSx, "& .MuiOutlinedInput-root": { ...fieldSx["& .MuiOutlinedInput-root"], backgroundColor: "#f4f7fb" } }}
              value={referencePreview}
            />
            {requiresGeneratedReference && nextIndexError && (
              <Button
                onClick={onRetryNextIndex}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Réessayer
              </Button>
            )}
          </Grid>

          <Grid item md={6} xs={12}>
            <FormLabel required sx={fieldLabelSx}>Date du constat</FormLabel>
            <TextField
              error={Boolean(errors.constat_date)}
              fullWidth
              helperText={errors.constat_date || " "}
              inputRef={dateRef}
              inputProps={{ max: "9999-12-31" }}
              onChange={(event) => updateField("constat_date", event.target.value)}
              size="small"
              sx={fieldSx}
              type="date"
              value={formData.constat_date}
            />
          </Grid>

          <Grid item md={6} xs={12}>
            <FormLabel sx={fieldLabelSx}>Constaté par</FormLabel>
            <Autocomplete
              disabled={usersLoading || Boolean(usersError)}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => sameId(option.id, value.id)}
              noOptionsText="Aucun utilisateur actif"
              onChange={(_, option) => updateField("constat_user", option?.id ?? "")}
              options={users}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(usersError)}
                  helperText={usersError || " "}
                  placeholder="Choisir l'utilisateur ayant constaté la NC"
                  size="small"
                  sx={fieldSx}
                />
              )}
              value={selectedUser}
            />
            {usersLoading && (
              <Typography color="#60758a" fontSize={10}>
                Chargement des utilisateurs…
              </Typography>
            )}
            {usersError && (
              <Button
                onClick={onRetryUsers}
                size="small"
                sx={{ textTransform: "none" }}
              >
                Réessayer
              </Button>
            )}
          </Grid>

          <Grid item md={6} xs={12}>
            <FormLabel required sx={fieldLabelSx}>Direction / Antenne</FormLabel>
            <Autocomplete
              disabled={directionsLoading || Boolean(directionsError)}
              getOptionLabel={directionLabel}
              isOptionEqualToValue={(option, value) => sameId(option.direction_id, value.direction_id)}
              onChange={(_, option) => updateField("direction_id", option?.direction_id ?? "")}
              options={directions}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(errors.direction_id)}
                  helperText={errors.direction_id || directionsError || " "}
                  inputRef={directionRef}
                  placeholder="Choisir une direction ou antenne"
                  size="small"
                  sx={fieldSx}
                />
              )}
              value={selectedDirection}
            />
            {directionsLoading && <Typography color="#60758a" fontSize={10}>Chargement des directions…</Typography>}
            {directionsError && (
              <Button onClick={onRetryDirections} size="small" sx={{ textTransform: "none" }}>
                Réessayer
              </Button>
            )}
          </Grid>

          <Grid item md={6} xs={12}>
            <FormLabel required sx={fieldLabelSx}>Processus concerné</FormLabel>
            <Autocomplete
              getOptionLabel={processLabel}
              groupBy={(option) => option.categorie_name || "Autres processus"}
              isOptionEqualToValue={(option, value) => sameId(option.processus_id, value.processus_id)}
              onChange={(_, option) => {
                setErrors((current) => ({ ...current, processus_id: undefined }));
                onProcessusChange(option);
              }}
              options={orderedProcessus}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={Boolean(errors.processus_id)}
                  helperText={errors.processus_id || " "}
                  inputRef={processusRef}
                  placeholder="Choisir un processus"
                  size="small"
                  sx={fieldSx}
                />
              )}
              value={selectedProcessus}
            />
          </Grid>

          <Grid item xs={12}>
            <FormLabel id="description-constat-label" required sx={fieldLabelSx}>Description du constat (faits précis, chiffrés)</FormLabel>
            <Box
              aria-invalid={Boolean(errors.description_constat)}
              aria-labelledby="description-constat-label"
              component="textarea"
              ref={descriptionRef}
              onChange={(event) => updateField("description_constat", event.target.value)}
              placeholder="Décrivez exactement ce que vous avez observé. Incluez des chiffres si possible…"
              rows={3}
              sx={{
                backgroundColor: "#fff",
                border: `1px solid ${
                  errors.description_constat ? "#d32f2f" : "#c7d3e4"
                }`,
                borderRadius: "8px",
                boxSizing: "border-box",
                color: "#10263d",
                fontFamily: "inherit",
                fontSize: 13,
                lineHeight: 1.5,
                minHeight: 86,
                outline: "none",
                p: 1.4,
                resize: "vertical",
                width: "100%",
                "&:focus": {
                  borderColor: errors.description_constat ? "#d32f2f" : "#00a6b6",
                  boxShadow: errors.description_constat
                    ? "0 0 0 2px rgba(211, 47, 47, 0.14)"
                    : "0 0 0 2px rgba(0, 166, 182, 0.16)",
                },
              }}
              value={formData.description_constat}
            />
            <Typography
              color={errors.description_constat ? "#d32f2f" : "transparent"}
              fontSize={11}
              sx={{ minHeight: 18, mt: 0.4 }}
            >
              {errors.description_constat || "Aucune erreur"}
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, gap: 1, justifyContent: "flex-end", mt: 0.5 }}>
          <Button
            color="error"
            disabled={saving}
            onClick={handleReset}
            startIcon={<RefreshIcon />}
            sx={{ borderColor: "#ff9da3", fontSize: 12, fontWeight: 700, textTransform: "none" }}
            variant="outlined"
          >
            Réinitialiser saisie
          </Button>
          <Button
            disabled={
              saving ||
              requiresGeneratedReference && (
                nextIndexLoading ||
                Boolean(nextIndexError) ||
                !Number.isInteger(nextIndex)
              )
            }
            endIcon={<ArrowForwardIcon />}
            sx={{ backgroundColor: "#00a6b6", color: "#fff", fontSize: 12, fontWeight: 800, px: 2.2, textTransform: "none", "&:hover": { backgroundColor: "#008f9d" } }}
            type="submit"
            variant="contained"
          >
            Étape suivante
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

ConstatStep.defaultProps = {
  directionsError: null,
  navigationEnabled: false,
  nextIndex: null,
  nextIndexError: null,
  onStepChange: () => {},
  savedReference: "",
  saving: false,
  triggersError: null,
  usersError: null,
};

ConstatStep.propTypes = {
  directions: PropTypes.arrayOf(PropTypes.object).isRequired,
  directionsError: PropTypes.string,
  directionsLoading: PropTypes.bool.isRequired,
  formData: PropTypes.shape({
    constat_date: PropTypes.string,
    constat_user: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    criticite: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    description_constat: PropTypes.string,
    description_exigence: PropTypes.string,
    direction_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    exigence_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    frequence_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    gravite_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    processus_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    trigger_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  nextIndex: PropTypes.number,
  nextIndexError: PropTypes.string,
  nextIndexLoading: PropTypes.bool.isRequired,
  navigationEnabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  onProcessusChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onRetryNextIndex: PropTypes.func.isRequired,
  onRetryDirections: PropTypes.func.isRequired,
  onRetryTriggers: PropTypes.func.isRequired,
  onRetryUsers: PropTypes.func.isRequired,
  onStepChange: PropTypes.func,
  onTriggerChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  processus: PropTypes.arrayOf(PropTypes.object).isRequired,
  savedReference: PropTypes.string,
  saving: PropTypes.bool,
  triggers: PropTypes.arrayOf(PropTypes.object).isRequired,
  triggersError: PropTypes.string,
  triggersLoading: PropTypes.bool.isRequired,
  users: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  usersError: PropTypes.string,
  usersLoading: PropTypes.bool.isRequired,
};

export default ConstatStep;
